import React, { useEffect, useMemo, useState } from "react";
import {
    Grid,
    Typography,
    TextField,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Snackbar,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    InputAdornment,
    Tooltip,
    Avatar,
    Divider,
    Grow,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import LinearProgress from "@material-ui/core/LinearProgress";
import moment from 'moment';

import Alert from "@material-ui/lab/Alert";
import Skeleton from "@material-ui/lab/Skeleton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import ClearIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import DescriptionIcon from "@material-ui/icons/Description";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import NotesIcon from "@material-ui/icons/Notes";
import InboxIcon from "@material-ui/icons/Inbox";
import SearchOffIcon from "@material-ui/icons/FindInPage";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PersonIcon from "@material-ui/icons/Person";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventIcon from "@material-ui/icons/Event";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import LayersIcon from "@material-ui/icons/Layers";
import HttpService from "../../../services/httpService";

// Shared Decorpot palette - identical constants to MarketingJunk,
// userManagement/teams, experienceCenter and redirectEC, so this page
// recolours with them instead of carrying its own navy.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const RED = "#c0392b";
const RED_LIGHT = "#fdecea";
const INK = "#3d2b21";
const COLUMN_COUNT = 8;

// Indeterminate bar shown while the list is being fetched. Same treatment as
// the other orange admin pages, so they read as one system.
const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(193,83,31,0.10)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles((theme) => ({
    // The page is sized to the viewport instead of growing past it, so the only
    // thing that scrolls is the table body. 144px is the app chrome above us:
    // antd Header 64 + Content margin 16*2 + Content padding 24*2 (sidebar.js).
    // boxSizing is explicit because this height includes our own padding and
    // there is no global border-box reset to rely on.
    pageRoot: {
        height: "calc(100vh - 144px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#f4f6fb",
        padding: "18px 24px",
        // A fixed-height page on a phone fights the on-screen keyboard and the
        // browser chrome, so small screens keep ordinary document scrolling.
        [theme.breakpoints.down('xs')]: {
            height: "auto",
            minHeight: "100vh",
            overflow: "visible",
            padding: "16px 16px 32px",
        },
    },
    headerBanner: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexShrink: 0,
        padding: "16px 26px",
        borderRadius: 16,
        background: HEADER_GRADIENT,
        color: "#fff",
        boxShadow: "0 8px 24px rgba(93,55,28,0.18)",
        marginBottom: 16,
        [theme.breakpoints.down('xs')]: {
            padding: "20px 18px",
            borderRadius: 16,
            flexWrap: "wrap",
        },
    },
    headerIconWrap: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 46,
        height: 46,
        borderRadius: 14,
        background: "rgba(255,255,255,0.18)",
        flexShrink: 0,
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: "#fff",
        lineHeight: 1.3,
    },
    headerSubtitle: {
        fontSize: 13,
        color: "rgba(255,255,255,0.85)",
        marginTop: 4,
    },
    headerActions: {
        marginLeft: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 6,
    },
    // A v4 Button on a page that also loads v5 would lose a plain className
    // battle, so the white-on-navy treatment is pinned here rather than left to
    // variant defaults.
    // Same white-on-gradient treatment as MarketingJunk and redirectEC - a
    // translucent button reads as disabled against the orange banner.
    refreshBtn: {
        background: "#fff",
        color: ORANGE_DARK,
        fontWeight: 700,
        borderRadius: 10,
        textTransform: "none",
        padding: "7px 18px",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        "&:hover": { background: ORANGE_LIGHT },
        "&.Mui-disabled": { background: "rgba(255,255,255,0.8)", color: ORANGE_DARK, boxShadow: "none" },
    },
    // A reserved slot rather than a conditional element, so starting a fetch
    // does not jog the card down by 4px and back again.
    progressSlot: {
        height: 4,
        marginBottom: 10,
        flexShrink: 0,
    },
    loadingBanner: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 14px",
        marginBottom: 14,
        borderRadius: 12,
        background: ORANGE_LIGHT,
        border: `1px solid ${ORANGE}33`,
        flexShrink: 0,
    },
    loadingBannerTitle: { fontSize: 13, fontWeight: 700, color: ORANGE_DARK },
    loadingBannerHint: { fontSize: 11.5, color: "#7a6a60", marginTop: 1 },
    lastUpdated: {
        fontSize: 11.5,
        color: "rgba(255,255,255,0.75)",
        letterSpacing: 0.2,
    },
    spin: {
        animation: "$spin 900ms linear infinite",
    },
    "@keyframes spin": {
        from: { transform: "rotate(0deg)" },
        to: { transform: "rotate(360deg)" },
    },
    // Takes the remaining height and becomes the next flex context. minHeight:0
    // is load-bearing: without it a flex child refuses to shrink below its
    // content, the card grows past the viewport and the page scrolls again.
    pageCard: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 16,
        border: "1px solid #eceff3",
        boxShadow: "0 4px 24px rgba(93,55,28,0.08)",
        padding: "18px 20px 6px",
        [theme.breakpoints.down('xs')]: {
            flex: "none",
            overflow: "visible",
            padding: "18px 16px 6px",
        },
    },
    sectionDivider: {
        borderTop: "1px solid #eceff3",
        marginTop: 14,
        paddingTop: 14,
        flexShrink: 0,
    },
    // Never squeezed - it sits below the scroll area and must stay reachable.
    pagination: {
        flexShrink: 0,
    },
    // The one block allowed to absorb the slack.
    tableSection: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down('xs')]: {
            flex: "none",
            display: "block",
        },
    },
    statCard: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        borderRadius: 14,
        cursor: "pointer",
        outline: "none",
        transition: "transform 200ms ease, box-shadow 200ms ease",
        border: "2px solid",
        "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 10px 22px rgba(93,55,28,0.14)",
        },
        "&:focus-visible": {
            boxShadow: "0 0 0 3px rgba(244,126,68,0.35)",
        },
    },
    statCardActive: {
        transform: "translateY(-3px)",
        boxShadow: "0 10px 22px rgba(93,55,28,0.18)",
    },
    // The selected card gets a filled tick in the corner rather than only a
    // colour change, so the active filter survives a colour-blind read.
    statCheck: {
        position: "absolute",
        top: 10,
        right: 12,
        fontSize: 18,
    },
    statIconWrap: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 44,
        height: 44,
        borderRadius: "50%",
        flexShrink: 0,
    },
    statLabel: {
        fontSize: 12,
        color: "#7c8695",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    statValue: {
        fontSize: 24,
        fontWeight: 700,
        color: INK,
        lineHeight: 1.25,
    },
    searchInputRoot: {
        borderRadius: 24,
        background: "#f7f9fc",
    },
    filterChip: {
        fontWeight: 600,
        borderRadius: 20,
        transition: "box-shadow 160ms ease",
    },
    resetBtn: {
        borderRadius: 20,
        textTransform: "none",
        fontWeight: 600,
    },
    resultCount: {
        fontSize: 13,
        color: "#7c8695",
        fontWeight: 600,
    },
    tableScrollArea: {
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        borderRadius: 12,
        border: "1px solid #eceff3",
        [theme.breakpoints.down('xs')]: {
            flex: "none",
            maxHeight: "60vh",
        },
    },
    tableHeadCell: {
        backgroundColor: ORANGE_DARK,
        color: "#fff",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: 0.3,
        borderBottom: "none",
        whiteSpace: "nowrap",
        // stickyHeader on <Table> already sets position:sticky/top:0 on head
        // cells - zIndex keeps this background painted above the scrolling rows
        // instead of letting them show through at the seam.
        zIndex: 3,
    },
    sortableHead: {
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { filter: "brightness(1.15)" },
    },
    tableRow: {
        cursor: "pointer",
        transition: "background-color 150ms ease",
        "&:nth-of-type(even)": {
            backgroundColor: "#fafbfd",
        },
        "&:hover": {
            backgroundColor: ORANGE_LIGHT,
        },
    },
    bodyCell: {
        fontSize: 13.5,
        color: INK,
    },
    requesterCell: {
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    requesterAvatar: {
        width: 30,
        height: 30,
        fontSize: 13,
        fontWeight: 700,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
    },
    mutedCell: {
        color: "#99a3b2",
        fontStyle: "italic",
    },
    actionIconBtn: {
        borderRadius: "50%",
        transition: "transform 200ms ease, background-color 200ms ease",
        "&:hover": {
            transform: "scale(1.1)",
        },
    },
    emptyStateWrap: {
        textAlign: "center",
        padding: "70px 20px",
        color: "#9aa5b1",
    },
    dialogPaperRounded: {
        borderRadius: 20,
        boxShadow: "0 20px 60px rgba(93,55,28,0.25)",
    },
    dialogHeaderBar: {
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "20px 26px",
        color: "#fff",
    },
    dialogHeaderIcon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 44,
        height: 44,
        borderRadius: 12,
        background: "rgba(255,255,255,0.2)",
        flexShrink: 0,
    },
    dialogHeaderTitle: {
        fontSize: 19,
        fontWeight: 700,
        color: "#fff",
        lineHeight: 1.3,
    },
    dialogHeaderSubtitle: {
        fontSize: 13,
        color: "rgba(255,255,255,0.85)",
        marginTop: 2,
    },
    dialogCloseBtn: {
        marginLeft: "auto",
        color: "#fff",
        transition: "background-color 200ms ease, transform 200ms ease",
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.18)",
            transform: "rotate(90deg)",
        },
    },
    summaryGrid: {
        background: "#f7f9fc",
        borderRadius: 14,
        border: "1px solid #eceff3",
        padding: "18px 20px",
        marginBottom: 24,
    },
    summaryLabel: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "#8a95a6",
        marginBottom: 4,
    },
    summaryValue: {
        fontSize: 14,
        fontWeight: 600,
        color: INK,
        wordBreak: "break-word",
    },
    detailSection: {
        marginBottom: 24,
    },
    detailSectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 14,
        fontWeight: 700,
        color: ORANGE_DARK,
        marginBottom: 12,
    },
    countBadge: {
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
    },
    remarksBox: {
        background: "#f7f9fc",
        border: "1px solid #eceff3",
        borderRadius: 12,
        padding: "12px 16px",
        fontSize: 14,
        color: "#4a5568",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    rejectionBox: {
        display: "flex",
        gap: 12,
        background: RED_LIGHT,
        border: "1px solid #f0c4bd",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 24,
    },
    certificateBtn: {
        borderRadius: 22,
        textTransform: "none",
        fontWeight: 600,
    },
    mediaFrame: {
        position: "relative",
        display: "block",
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        transition: "transform 200ms ease, box-shadow 200ms ease",
        "&:hover": {
            transform: "scale(1.03)",
            boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
        },
        "&:hover $mediaOverlay": {
            opacity: 1,
        },
    },
    mediaImg: {
        display: "block",
        width: "100%",
        height: 120,
        objectFit: "cover",
    },
    mediaOverlay: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        background: "rgba(61,43,33,0.48)",
        opacity: 0,
        transition: "opacity 200ms ease",
    },
    videoThumb: {
        borderRadius: 10,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        overflow: "hidden",
        background: "#000",
        display: "block",
        width: "100%",
    },
    emptyMedia: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        color: "#aab3bf",
        background: "#fafbfd",
        border: "1px dashed #dde3ec",
        borderRadius: 10,
        padding: "14px 16px",
    },
    primaryActionBtn: {
        borderRadius: 22,
        textTransform: "none",
        fontWeight: 700,
        minWidth: 130,
        boxShadow: "none",
    },
    approveBtn: {
        background: "#2e7d32",
        color: "#fff",
        "&:hover": { background: "#1b5e20" },
    },
    rejectBtn: {
        background: RED,
        color: "#fff",
        "&:hover": { background: "#8e2a1f" },
    },
    secondaryActionBtn: {
        borderRadius: 22,
        textTransform: "none",
        fontWeight: 600,
    },
    leadIdChip: {
        fontWeight: 700,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
    },
}));

// Explicit color mapping for the approval status chip - MUI v4's Chip only
// supports color="default|primary|secondary", so success/error tones are
// applied via inline style instead of the (invalid, silently ignored) v5-only
// color values.
const APPROVAL_STYLES = {
    Approved: { background: "#e6f4ea", color: "#1e7e34" },
    Rejected: { background: RED_LIGHT, color: RED },
    Pending: { background: "#fff3e0", color: "#b26a00" },
};

const STATUS_FILTERS = ["All", "Pending", "Approved", "Rejected"];

const ApprovalChip = ({ status }) => {
    const style = APPROVAL_STYLES[status] || APPROVAL_STYLES.Pending;
    const icon = status === "Approved"
        ? <CheckCircleIcon style={{ fontSize: 16, color: style.color }} />
        : status === "Rejected"
            ? <CancelIcon style={{ fontSize: 16, color: style.color }} />
            : <HourglassEmptyIcon style={{ fontSize: 16, color: style.color }} />;

    return (
        <Chip
            icon={icon}
            label={status}
            size="small"
            style={{ ...style, fontWeight: 700 }}
        />
    );
};

const initials = (name) => String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join("") || "?";

// sentDate is the field the form writes; createdAt is the timestamps fallback
// for rows saved before it existed.
const requestedOn = (row) => row?.sentDate || row?.createdAt || null;

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY, hh:mm A") : "-");

// The date the field team typed on submission - "Completed" fills the completion
// date, "Completed-MWP" fills the MWP one. Stored as the MM-DD-YYYY string the
// form produced, so it is parsed with that format rather than guessed at.
const submittedDate = (row) => {
    const raw = row?.executionCompletionDate || row?.executionMWPDate;
    if (!raw) return "";
    const parsed = moment(raw, "MM-DD-YYYY", true);
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(raw);
};

export default function ExecutionApprovalList() {
    const classes = useStyles();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);

    const [statusFilter, setStatusFilter] = useState("Pending");
    const [search, setSearch] = useState("");
    const [sortDesc, setSortDesc] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);

    const [rejectReason, setRejectReason] = useState("");
    const [rejectTouched, setRejectTouched] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [snack, setSnack] = useState({
        open: false,
        msg: "",
        type: "success", // success | error | warning | info
    });

    const [executionCompletionDate, setExecutionCompletionDate] = useState(null);
    const [executionMWPDate, setExecutionMWPDate] = useState(null);

    const getCurrentDate = () => {
        let projectMoveDate = new Date();
        let store = moment(projectMoveDate).format('MM-DD-YYYY');
        setExecutionMWPDate(store);
        setExecutionCompletionDate(store)
    }

    // ---------------- FETCH ----------------
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await HttpService.getExecutionApprovals();
            setData(res?.data?.data || []);
            setLastFetchedAt(new Date());
        } catch (e) {
            // Optional chaining throughout: a timeout or a dropped connection
            // gives an Error with no `response` at all, and reading `.data` off
            // it used to throw a second time INSIDE this catch - which skipped
            // setLoading(false) and left the page spinning forever.
            setSnack({
                open: true,
                msg: e?.response?.data?.message || "Failed to load execution approvals",
                type: "error",
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        getCurrentDate();
        // eslint-disable-next-line
    }, []);

    // ---------------- FILTER ----------------
    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        let temp = data.filter(x => {
            if (statusFilter !== "All" && x?.cityManagerApproval !== statusFilter) return false;
            if (!term) return true;
            // Widened from erpProjectNo alone - the box sits next to a list of
            // people and stages, so searching either of those is the obvious
            // thing to try.
            return [
                x?.leadId?.erpProjectNo,
                x?.leadId?.lead_no,
                x?.status,
                x?.sentBy?.name,
                x?.pendingApprovalBucket,
            ].some(field => String(field || "").toLowerCase().includes(term));
        });

        temp.sort((a, b) => {
            const aTime = new Date(requestedOn(a) || 0).getTime();
            const bTime = new Date(requestedOn(b) || 0).getTime();
            return sortDesc ? bTime - aTime : aTime - bTime;
        });

        return temp;
    }, [data, statusFilter, search, sortDesc]);

    // A filter change can leave you stranded on a page that no longer exists.
    useEffect(() => {
        setPage(0);
    }, [statusFilter, search, rowsPerPage]);

    const pageRows = useMemo(
        () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filtered, page, rowsPerPage]
    );

    // ---------------- ACTIONS ----------------
    const openConfirm = (row, action) => {
        setSelected(row);
        setConfirmAction(action);
        setRejectReason("");
        setRejectTouched(false);
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        if (confirming) return;
        setConfirmOpen(false);
        setRejectReason("");
        setRejectTouched(false);
    };

    const rejectReasonMissing = confirmAction === "reject" && !rejectReason.trim();

    const handleConfirmAction = async () => {
        if (rejectReasonMissing) {
            setRejectTouched(true);
            return;
        }

        try {
            if (confirmAction === "approve") {
                await HttpService.approveExecution({
                    approvalId: selected._id,

                    leadId: selected.leadId?._id,
                    status: selected.status,

                    executionCompletionRemarks: selected.executionCompletionRemarks,
                    // Prefer the dates the field team actually submitted. The
                    // fallback is not cosmetic: updateStatusFromUnderExecutionTo
                    // only stamps the lead's dates when these keys are truthy, so
                    // approvals created before the schema stored them would stop
                    // being dated if we sent nothing.
                    executionCompletionDate: selected.executionCompletionDate || executionCompletionDate,
                    executionMWPDate: selected.executionMWPDate || executionMWPDate,

                    completionCertificate: selected.completionCertificate,
                    photoUrls: selected.photoUrls,
                    videoUrls: selected.videoUrls,
                    // The schema path is `whatsappUrls`; sendForCityManagerApproval
                    // receives it as `whatsAppPics` and now maps it across. The
                    // receiving route accepts either key.
                    whatsappUrls: selected.whatsappUrls,
                });

                setSnack({
                    open: true,
                    msg: "Approved Successfully",
                    type: "success"
                });
            }

            if (confirmAction === "reject") {
                await HttpService.rejectExecution({
                    approvalId: selected._id,
                    leadId: selected.leadId?._id,
                    rejectionReason: rejectReason.trim(),
                });

                setSnack({
                    open: true,
                    msg: "Rejected Successfully",
                    type: "success"
                });
            }

            setConfirmOpen(false);
            setRejectReason("");
            setRejectTouched(false);
            setOpen(false);
            fetchData();

        } catch (err) {
            setSnack({
                open: true,
                msg: err?.response?.data?.message || "Action failed. Please try again.",
                // Without this the Alert fell back to MUI's default severity,
                // so a failure was announced in success green.
                type: "error",
            });
        }
    };

    // ---------------- STATS ----------------
    const counts = useMemo(() => ({
        All: data.length,
        Pending: data.filter(x => x.cityManagerApproval === "Pending").length,
        Approved: data.filter(x => x.cityManagerApproval === "Approved").length,
        Rejected: data.filter(x => x.cityManagerApproval === "Rejected").length,
    }), [data]);

    const statCards = [
        { key: "All", label: "Total Requests", icon: <LayersIcon />, tint: ORANGE_LIGHT, tone: ORANGE_DARK, border: "#f0c3ab" },
        { key: "Pending", label: "Pending", icon: <HourglassEmptyIcon />, tint: "#fff3e0", tone: "#ff9800", border: "#ffcc80" },
        { key: "Approved", label: "Approved", icon: <CheckCircleIcon />, tint: "#e8f5e9", tone: "#2e7d32", border: "#a5d6a7" },
        { key: "Rejected", label: "Rejected", icon: <CancelIcon />, tint: RED_LIGHT, tone: RED, border: "#e8a8a1" },
    ];

    const isFiltering = Boolean(search.trim()) || statusFilter !== "All";

    const renderDetailMedia = (title, icon, urls, emptyText, renderItem, cols) => (
        <div className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>
                {icon} {title}
                {urls?.length > 0 && (
                    <Chip size="small" className={classes.countBadge} label={urls.length} />
                )}
            </Typography>
            {urls?.length > 0 ? (
                <Grid container spacing={2}>
                    {urls.map((url, i) => (
                        <Grid item xs={6} sm={cols} key={i}>
                            {renderItem(url, i)}
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <div className={classes.emptyMedia}>
                    <InboxIcon style={{ fontSize: 18 }} /> {emptyText}
                </div>
            )}
        </div>
    );

    const imageTile = (url, i, label) => (
        <a
            className={classes.mediaFrame}
            href={url}
            target="_blank"
            rel="noreferrer"
            title="Open full size"
        >
            <img src={url} alt={`${label} ${i + 1}`} className={classes.mediaImg} />
            <span className={classes.mediaOverlay}>
                <OpenInNewIcon />
            </span>
        </a>
    );

    return (
        <div className={classes.pageRoot}>

            {/* HEADER */}
            <div className={classes.headerBanner}>
                <div className={classes.headerIconWrap}>
                    <AssignmentTurnedInIcon />
                </div>
                <div>
                    <Typography className={classes.headerTitle}>
                        Execution Approval Dashboard
                    </Typography>
                    <Typography className={classes.headerSubtitle}>
                        Review, approve or reject execution completion requests raised by the field teams
                    </Typography>
                </div>

                <div className={classes.headerActions}>
                    <Button
                        className={classes.refreshBtn}
                        disabled={loading}
                        onClick={fetchData}
                        startIcon={<RefreshIcon className={loading ? classes.spin : ""} />}
                    >
                        {loading ? "Refreshing…" : "Refresh"}
                    </Button>
                    {lastFetchedAt && (
                        <Typography className={classes.lastUpdated}>
                            Updated {moment(lastFetchedAt).format("hh:mm A")}
                        </Typography>
                    )}
                </div>
            </div>

            {/* Reserved slot - see progressSlot. */}
            <div className={classes.progressSlot}>
                {loading ? <BrandLinearProgress /> : null}
            </div>

            {/* The bar alone says "something is happening"; this says WHAT,
                which matters most on a manual refresh. */}
            {loading && (
                <div className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.loadingBannerTitle}>Fetching execution approvals…</div>
                        <div className={classes.loadingBannerHint}>
                            {data.length
                                ? "Reloading — the requests below stay visible until the new data lands."
                                : "Pulling every execution completion request raised for your centres."}
                        </div>
                    </div>
                </div>
            )}

            <Paper className={classes.pageCard} elevation={0}>

                {/* STATS CARDS */}
                <Grid container spacing={2}>
                    {statCards.map(card => {
                        const active = statusFilter === card.key;
                        return (
                            <Grid item xs={12} sm={6} md={3} key={card.key}>
                                <Paper
                                    className={`${classes.statCard} ${active ? classes.statCardActive : ""}`}
                                    elevation={0}
                                    style={{
                                        borderColor: active ? card.tone : card.border,
                                        background: active ? card.tint : "#fff",
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={active}
                                    onClick={() => setStatusFilter(card.key)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setStatusFilter(card.key);
                                        }
                                    }}
                                >
                                    <div className={classes.statIconWrap} style={{ background: card.tint, color: card.tone }}>
                                        {card.icon}
                                    </div>
                                    <div>
                                        <Typography className={classes.statLabel}>{card.label}</Typography>
                                        <Typography className={classes.statValue}>
                                            {loading && data.length === 0 ? <Skeleton width={40} /> : counts[card.key]}
                                        </Typography>
                                    </div>
                                    {active && (
                                        <CheckCircleIcon className={classes.statCheck} style={{ color: card.tone }} />
                                    )}
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* FILTER BAR */}
                <div className={classes.sectionDivider}>

                    <Grid container spacing={2} alignItems="center">

                        {/* SEARCH BOX */}
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label="Search lead, requester or stage"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    classes: { root: classes.searchInputRoot },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" style={{ color: "#8a95a6" }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search && (
                                        <InputAdornment position="end">
                                            <Tooltip title="Clear search">
                                                <IconButton size="small" onClick={() => setSearch("")}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* QUICK FILTER CHIPS - the single source of truth for the
                            status filter. A separate Tabs strip used to sit below
                            this and could not represent "All", so picking All lit
                            up the Pending tab. */}
                        <Grid item xs={12} md={5}>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {STATUS_FILTERS.map(key => {
                                    const active = statusFilter === key;
                                    const tone = key === "Pending" ? "#ff9800"
                                        : key === "Approved" ? "#4caf50"
                                            : key === "Rejected" ? "#f44336"
                                                : ORANGE_DARK;
                                    return (
                                        <Chip
                                            key={key}
                                            className={classes.filterChip}
                                            label={`${key} (${counts[key]})`}
                                            clickable
                                            onClick={() => setStatusFilter(key)}
                                            style={{
                                                background: active ? tone : "#f1f2f5",
                                                color: active ? "#fff" : "#444",
                                                boxShadow: active ? "0 4px 10px rgba(93,55,28,0.18)" : "none",
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </Grid>

                        {/* RESULT COUNT + RESET */}
                        <Grid item xs={12} md={3} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
                            <Typography className={classes.resultCount}>
                                {loading && data.length === 0 ? "…" : `${filtered.length} of ${data.length}`}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                className={classes.resetBtn}
                                disabled={!isFiltering && sortDesc}
                                onClick={() => {
                                    setSearch("");
                                    setStatusFilter("All");
                                    setSortDesc(true);
                                }}
                            >
                                Reset
                            </Button>
                        </Grid>

                    </Grid>
                </div>

                {/* TABLE */}
                <div className={`${classes.sectionDivider} ${classes.tableSection}`}>

                    <TableContainer className={classes.tableScrollArea}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.tableHeadCell}>#</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Lead ID</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Stage</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Requested By</TableCell>
                                    <Tooltip title={sortDesc ? "Newest first - click for oldest" : "Oldest first - click for newest"}>
                                        <TableCell
                                            className={`${classes.tableHeadCell} ${classes.sortableHead}`}
                                            onClick={() => setSortDesc(prev => !prev)}
                                        >
                                            Requested On {sortDesc ? "↓" : "↑"}
                                        </TableCell>
                                    </Tooltip>
                                    <TableCell className={classes.tableHeadCell}>Awaiting</TableCell>
                                    <TableCell className={classes.tableHeadCell}>Approval Status</TableCell>
                                    <TableCell className={classes.tableHeadCell} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading && data.length === 0 ? (
                                    // Skeleton rows keep the table's shape while loading
                                    // instead of collapsing the page to a lone spinner.
                                    [...Array(6)].map((_, i) => (
                                        <TableRow key={`sk-${i}`}>
                                            {[...Array(COLUMN_COUNT)].map((__, c) => (
                                                <TableCell key={c}><Skeleton height={22} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : pageRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={COLUMN_COUNT} style={{ borderBottom: "none" }}>
                                            <div className={classes.emptyStateWrap}>
                                                {/* "nothing matches" and "nothing exists" are
                                                    different problems with different fixes. */}
                                                {data.length > 0 ? (
                                                    <>
                                                        <SearchOffIcon style={{ fontSize: 48, color: "#c9d2de" }} />
                                                        <Typography style={{ marginTop: 10, fontWeight: 600, color: "#7c8695" }}>
                                                            No requests match these filters
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            className={classes.resetBtn}
                                                            style={{ marginTop: 10 }}
                                                            onClick={() => { setSearch(""); setStatusFilter("All"); }}
                                                        >
                                                            Clear filters
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <InboxIcon style={{ fontSize: 48, color: "#c9d2de" }} />
                                                        <Typography style={{ marginTop: 10, fontWeight: 600, color: "#7c8695" }}>
                                                            No execution requests yet
                                                        </Typography>
                                                        <Typography variant="body2" style={{ color: "#aab3bf" }}>
                                                            Requests raised by the field teams will appear here
                                                        </Typography>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pageRows.map((row, index) => (
                                        <TableRow
                                            key={row._id}
                                            className={classes.tableRow}
                                            hover
                                            onClick={() => { setSelected(row); setOpen(true); }}
                                        >
                                            <TableCell className={classes.bodyCell}>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>

                                            <TableCell className={classes.bodyCell} style={{ fontWeight: 700, color: ORANGE_DARK }}>
                                                {row?.leadId?.erpProjectNo || "-"}
                                            </TableCell>

                                            <TableCell className={classes.bodyCell}>
                                                <Chip
                                                    label={row.status}
                                                    size="small"
                                                    style={{ background: ORANGE_LIGHT, color: ORANGE_DARK, fontWeight: 600 }}
                                                />
                                            </TableCell>

                                            <TableCell className={classes.bodyCell}>
                                                <div className={classes.requesterCell}>
                                                    <Avatar className={classes.requesterAvatar}>
                                                        {initials(row?.sentBy?.name)}
                                                    </Avatar>
                                                    <span>{row?.sentBy?.name || "Unknown"}</span>
                                                </div>
                                            </TableCell>

                                            <TableCell className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                                                {requestedOn(row)
                                                    ? moment(requestedOn(row)).format("DD MMM YYYY")
                                                    : <span className={classes.mutedCell}>-</span>}
                                                <div style={{ fontSize: 11.5, color: "#99a3b2" }}>
                                                    {requestedOn(row) ? moment(requestedOn(row)).fromNow() : ""}
                                                </div>
                                            </TableCell>

                                            <TableCell className={classes.bodyCell}>
                                                {row?.pendingApprovalBucket
                                                    ? <Chip size="small" variant="outlined" label={row.pendingApprovalBucket} style={{ fontWeight: 600 }} />
                                                    : <span className={classes.mutedCell}>-</span>}
                                            </TableCell>

                                            <TableCell className={classes.bodyCell}>
                                                <ApprovalChip status={row.cityManagerApproval} />
                                            </TableCell>

                                            {/* The row itself opens the details, so the buttons
                                                must not let that click bubble through as well. */}
                                            <TableCell
                                                className={classes.bodyCell}
                                                align="center"
                                                style={{ whiteSpace: "nowrap" }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Tooltip title="View details">
                                                    <IconButton
                                                        size="small"
                                                        className={classes.actionIconBtn}
                                                        onClick={() => { setSelected(row); setOpen(true); }}
                                                    >
                                                        <VisibilityIcon style={{ color: ORANGE }} />
                                                    </IconButton>
                                                </Tooltip>

                                                {row.cityManagerApproval === "Pending" && (
                                                    <>
                                                        <Tooltip title="Approve">
                                                            <IconButton
                                                                size="small"
                                                                className={classes.actionIconBtn}
                                                                onClick={() => openConfirm(row, "approve")}
                                                            >
                                                                <CheckIcon style={{ color: "#2e7d32" }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Reject">
                                                            <IconButton
                                                                size="small"
                                                                className={classes.actionIconBtn}
                                                                onClick={() => openConfirm(row, "reject")}
                                                            >
                                                                <CloseIcon style={{ color: RED }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {filtered.length > 0 && (
                        <TablePagination
                            className={classes.pagination}
                            component="div"
                            count={filtered.length}
                            page={page}
                            onChangePage={(event, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onChangeRowsPerPage={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    )}

                </div>
            </Paper>

            {/* DETAILS DIALOG */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="md"
                classes={{ paper: classes.dialogPaperRounded }}
                TransitionComponent={Grow}
                transitionDuration={250}
            >
                <div className={classes.dialogHeaderBar} style={{ background: HEADER_GRADIENT }}>
                    <div className={classes.dialogHeaderIcon}>
                        <AssignmentTurnedInIcon />
                    </div>
                    <div>
                        <Typography className={classes.dialogHeaderTitle}>
                            Execution Details
                        </Typography>
                        {selected?.leadId?.erpProjectNo && (
                            <Typography className={classes.dialogHeaderSubtitle}>
                                Lead ID: {selected.leadId.erpProjectNo}
                            </Typography>
                        )}
                    </div>
                    <IconButton
                        className={classes.dialogCloseBtn}
                        size="small"
                        onClick={() => setOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>

                <DialogContent style={{ padding: 26 }}>

                    {selected && (
                        <>
                            {/* SUMMARY - the context that used to require closing the
                                dialog and reading it back off the table row. */}
                            <div className={classes.summaryGrid}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography className={classes.summaryLabel}>
                                            <LayersIcon style={{ fontSize: 14 }} /> Stage
                                        </Typography>
                                        <Typography className={classes.summaryValue}>{selected.status || "-"}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography className={classes.summaryLabel}>
                                            <PersonIcon style={{ fontSize: 14 }} /> Requested By
                                        </Typography>
                                        <Typography className={classes.summaryValue}>{selected?.sentBy?.name || "Unknown"}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography className={classes.summaryLabel}>
                                            <ScheduleIcon style={{ fontSize: 14 }} /> Requested On
                                        </Typography>
                                        <Typography className={classes.summaryValue}>{formatDate(requestedOn(selected))}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography className={classes.summaryLabel}>
                                            <SupervisorAccountIcon style={{ fontSize: 14 }} /> Awaiting
                                        </Typography>
                                        <Typography className={classes.summaryValue}>{selected.pendingApprovalBucket || "-"}</Typography>
                                    </Grid>
                                    {/* Stored on the approval from this release on,
                                        so it is blank on older records. */}
                                    {submittedDate(selected) && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography className={classes.summaryLabel}>
                                                <EventIcon style={{ fontSize: 14 }} /> Completion Date Entered
                                            </Typography>
                                            <Typography className={classes.summaryValue}>
                                                {submittedDate(selected)}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>

                                <Divider style={{ margin: "16px 0 14px" }} />

                                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                    <ApprovalChip status={selected.cityManagerApproval} />
                                    {selected.cityManagerApproval === "Approved" && (
                                        <Typography variant="body2" style={{ color: "#7c8695" }}>
                                            by <b style={{ color: ORANGE_DARK }}>{selected?.approvedBy?.name || "-"}</b> on {formatDate(selected.approvedDate)}
                                        </Typography>
                                    )}
                                </div>
                            </div>

                            {selected.cityManagerApproval === "Rejected" && selected.rejectionReason && (
                                <div className={classes.rejectionBox}>
                                    <ErrorOutlineIcon style={{ color: RED }} />
                                    <div>
                                        <Typography style={{ fontWeight: 700, color: RED, fontSize: 13 }}>
                                            Rejection Reason
                                        </Typography>
                                        <Typography variant="body2" style={{ color: "#8e3b36", marginTop: 2 }}>
                                            {selected.rejectionReason}
                                        </Typography>
                                    </div>
                                </div>
                            )}

                            <div className={classes.detailSection}>
                                <Typography className={classes.detailSectionTitle}>
                                    <NotesIcon fontSize="small" /> Remarks
                                </Typography>
                                {selected.executionCompletionRemarks ? (
                                    <div className={classes.remarksBox}>
                                        {selected.executionCompletionRemarks}
                                    </div>
                                ) : (
                                    <div className={classes.emptyMedia}>
                                        <NotesIcon style={{ fontSize: 18 }} /> No remarks provided
                                    </div>
                                )}
                            </div>

                            <div className={classes.detailSection}>
                                <Typography className={classes.detailSectionTitle}>
                                    <DescriptionIcon fontSize="small" /> Completion Certificate
                                </Typography>
                                {selected.completionCertificate ? (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        className={classes.certificateBtn}
                                        startIcon={<DescriptionIcon />}
                                        endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                                        component="a"
                                        href={selected.completionCertificate}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View Certificate
                                    </Button>
                                ) : (
                                    <div className={classes.emptyMedia}>
                                        <DescriptionIcon style={{ fontSize: 18 }} /> No certificate uploaded
                                    </div>
                                )}
                            </div>

                            {renderDetailMedia(
                                "Photos",
                                <PhotoLibraryIcon fontSize="small" />,
                                selected.photoUrls,
                                "No photos uploaded",
                                (url, i) => imageTile(url, i, "Execution photo"),
                                3
                            )}

                            {renderDetailMedia(
                                "Videos",
                                <VideoLibraryIcon fontSize="small" />,
                                selected.videoUrls,
                                "No videos uploaded",
                                (url) => <video src={url} controls className={classes.videoThumb} />,
                                6
                            )}

                            {/* Previously collected and sent on approval but never
                                shown, so the approver could not see what they were
                                signing off. */}
                            {renderDetailMedia(
                                "WhatsApp Screenshots",
                                <WhatsAppIcon fontSize="small" />,
                                selected.whatsappUrls,
                                "No WhatsApp screenshots uploaded",
                                (url, i) => imageTile(url, i, "WhatsApp screenshot"),
                                3
                            )}
                        </>
                    )}

                </DialogContent>

                <DialogActions style={{ padding: "16px 26px 22px" }}>
                    <Button
                        onClick={() => setOpen(false)}
                        variant="outlined"
                        className={classes.secondaryActionBtn}
                    >
                        Close
                    </Button>

                    {/* Decide without bouncing back to the table. */}
                    {selected?.cityManagerApproval === "Pending" && (
                        <>
                            <Button
                                variant="contained"
                                disableElevation
                                className={`${classes.primaryActionBtn} ${classes.rejectBtn}`}
                                startIcon={<CloseIcon />}
                                onClick={() => openConfirm(selected, "reject")}
                            >
                                Reject
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                className={`${classes.primaryActionBtn} ${classes.approveBtn}`}
                                startIcon={<CheckIcon />}
                                onClick={() => openConfirm(selected, "approve")}
                            >
                                Approve
                            </Button>
                        </>
                    )}
                </DialogActions>

            </Dialog>


            {/* CONFIRM DIALOG */}
            <Dialog
                open={confirmOpen}
                onClose={closeConfirm}
                fullWidth
                maxWidth="xs"
                classes={{ paper: classes.dialogPaperRounded }}
                TransitionComponent={Grow}
                transitionDuration={250}
            >

                <div
                    className={classes.dialogHeaderBar}
                    style={{
                        background: confirmAction === "approve"
                            ? "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)"
                            : `linear-gradient(135deg, ${RED} 0%, #d9583f 100%)`,
                    }}
                >
                    <div className={classes.dialogHeaderIcon}>
                        {confirmAction === "approve" ? <CheckCircleIcon /> : <CancelIcon />}
                    </div>
                    <div>
                        <Typography className={classes.dialogHeaderTitle}>
                            {confirmAction === "approve" ? "Confirm Approval" : "Confirm Rejection"}
                        </Typography>
                        <Typography className={classes.dialogHeaderSubtitle}>
                            {confirmAction === "approve"
                                ? "This moves the project out of execution"
                                : "The request goes back to the field team"}
                        </Typography>
                    </div>
                </div>

                <DialogContent style={{ padding: 26 }}>
                    <Typography variant="body2" style={{ color: "#4a5568" }}>
                        Are you sure you want to{" "}
                        <b>{confirmAction === "approve" ? "APPROVE" : "REJECT"}</b>{" "}
                        this execution request?
                    </Typography>

                    <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Chip
                            className={classes.leadIdChip}
                            size="small"
                            label={`Lead ID: ${selected?.leadId?.erpProjectNo ?? "-"}`}
                        />
                        {selected?.sentBy?.name && (
                            <Chip
                                className={classes.leadIdChip}
                                size="small"
                                label={`Raised by: ${selected.sentBy.name}`}
                            />
                        )}
                    </div>

                    {confirmAction === "reject" && (
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            variant="outlined"
                            label="Rejection Reason"
                            required
                            placeholder="Tell the field team what needs to change"
                            style={{ marginTop: 20 }}
                            disabled={confirming}
                            // Controlled, so cancelling genuinely clears it. It used
                            // to be uncontrolled, which let a previous row's reason
                            // survive in state.
                            value={rejectReason}
                            error={rejectTouched && rejectReasonMissing}
                            helperText={
                                rejectTouched && rejectReasonMissing
                                    ? "A reason is required - the field team sees this"
                                    : " "
                            }
                            onBlur={() => setRejectTouched(true)}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />
                    )}
                </DialogContent>

                <DialogActions style={{ padding: "16px 26px 22px" }}>

                    <Button
                        onClick={closeConfirm}
                        variant="outlined"
                        disabled={confirming}
                        className={classes.secondaryActionBtn}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disableElevation
                        disabled={confirming || rejectReasonMissing}
                        className={`${classes.primaryActionBtn} ${confirmAction === "approve" ? classes.approveBtn : classes.rejectBtn}`}
                        onClick={async () => {
                            setConfirming(true);
                            try {
                                await handleConfirmAction();
                            } finally {
                                setConfirming(false);
                            }
                        }}
                    >
                        {confirming
                            ? (
                                <>
                                    <CircularProgress size={16} thickness={5} style={{ color: '#fff', marginRight: 8 }} />
                                    {confirmAction === "approve" ? "Approving…" : "Rejecting…"}
                                </>
                            )
                            : (confirmAction === "approve" ? "Approve" : "Reject")
                        }
                    </Button>

                </DialogActions>

            </Dialog>

            {/* SNACKBAR */}
            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <Alert
                    elevation={6}
                    variant="filled"
                    severity={snack.type}
                    onClose={() => setSnack(s => ({ ...s, open: false }))}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>

        </div>
    );
}
